import React, { useState } from "react";
import { Table, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button } from "reactstrap";
import { LoadingMessage } from "../../Controls";
import { Mode } from "../../types";
import { showToast } from "../../../../utils/Helper";
import { FormattedNumber } from "../../../FormattedNumber/FormattedNumber";

const CreditRatingGrid = ({
  creditRanges,
  isLoading,
  handleAction,
  getRatingsByRiskRangeId,
  removeRatingFromRange,
  services,
}) => {
  const [expandedRows, setExpandedRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState([]);

  const toggleRow = (rowId, riskRange) => {
    const onAdd = (prevState, rowId) => {
      getCreditRatings(rowId, riskRange);
      return [...prevState, rowId];
    };

    setExpandedRows((prevState) =>
      prevState.includes(rowId) ? prevState.filter((id) => id !== rowId) : onAdd(prevState, rowId)
    );
  };

  const collapseRow = (rowId) => setExpandedRows((prevState) => prevState.filter((id) => id !== rowId));

  const toggleLoadingRow = (rowId) => {
    setLoadingRows((prevState) =>
      prevState.includes(rowId) ? prevState.filter((rowId) => rowId !== rowId) : [...prevState, rowId]
    );
  };

  const getCreditRatings = (rowId, riskRange) => {
    if (riskRange.Ratings && riskRange.Ratings.length > 0) return;
    getRatingsByRiskRangeId(riskRange.RiskRangeId, toggleLoadingRow);
  };

  return (
    <div style={{ margin: "20px" }}>
      <Table style={{ tableLayout: "fixed", width: "100%" }}>
        <thead>
          <tr>
            <th>#</th>
            <th>Range Name</th>
            <th>Range Color</th>
            <th>Limit percentage</th>
            <th>Limit Percentage for Issuer</th>
            <th>Ordenation Score</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <GridContent
            creditRanges={creditRanges}
            handleAction={handleAction}
            expandedRows={expandedRows}
            toggleRow={toggleRow}
            collapseRow={collapseRow}
            loadingRows={loadingRows}
            services={services}
            removeRatingFromRange={removeRatingFromRange}
          />
        </tbody>
      </Table>
    </div>
  );
};

const GridContent = ({
  creditRanges,
  handleAction,
  expandedRows,
  collapseRow,
  toggleRow,
  loadingRows,
  services,
  removeRatingFromRange,
}) => {
  return creditRanges.map((range, i) => {
    const isExpanded = expandedRows.includes(range.RiskRangeId);
    const isLoading = loadingRows.includes(range.RiskRangeId);
    const isEmpty = range.Ratings.length == 0;
    return (
      <React.Fragment key={`creditRanges-${i}`}>
        <tr
          style={{
            cursor: "pointer",
            backgroundColor: isExpanded ? "#e7e7e7" : "",
          }}
          onClick={() => toggleRow(range.RiskRangeId, range)}
        >
          <td>{range.RiskRangeId}</td>
          <td>{range.RiskRange}</td>
          <td>
            <div
              style={{
                backgroundColor: range.RiskRangeColor,
                width: "50%",
                padding: "5px 10px",
                borderRadius: "5px",
              }}
            ></div>
          </td>
          <td>
            <FormattedNumber value={range.RiskRangePercentage} dataTypeDecimalName="price" decimalScale={2} />
            %
          </td>
          <td>
            <FormattedNumber
              value={range.RiskRangePercentageIssuer}
              dataTypeDecimalName="quantity"
              decimalScale={2}
            />
            %
          </td>
          <td>
            <FormattedNumber value={range.RiskRangeScore} dataTypeDecimalName="price" decimalScale={2} />
          </td>
          <td>
            <Actions handleAction={handleAction} range={range} collapseRow={collapseRow} />
          </td>
        </tr>

        {isExpanded && (
          <ExpandedItems
            ratings={range.Ratings}
            services={services}
            removeRatingFromRange={removeRatingFromRange}
            isEmpty={isEmpty}
            isLoading={isLoading}
          />
        )}
      </React.Fragment>
    );
  });
};

const ExpandedItems = ({ ratings, services, removeRatingFromRange, isEmpty, isLoading }) => {
  const handleDeleteRating = (rating) => {
    const response = confirm("Are you sure?");

    if (!response) return;

    const request = {
      ratingId: rating.CreditRatingScore,
      rangeId: rating.CreditRatingRangeId,
      agencyId: rating.CreditRatingAgencyId,
    };

    services.deleteCreditRatingFromRange &&
      services.deleteCreditRatingFromRange(request).then(({ response }) => {
        if (response && response.result) {
          const { Code, Description } = response.result;
          if (Code == 200) {
            removeRatingFromRange(rating.CreditRatingRangeId, rating);
            showToast("Removed sucessfully");
          } else showToast(Description, true);
        }
      });
  };

  if (isLoading) {
    return <EmptyOrLoading loading={isLoading} />;
  }

  if (isEmpty) {
    return <EmptyOrLoading loading={isLoading} />;
  }

  return (
    <tr>
      <td colSpan="6" style={{ padding: "10px" }}>
        <div
          style={{
            backgroundColor: "#f5f8f8",
            borderRadius: "5px",
            padding: "15px",
          }}
        >
          <Table style={{ marginBottom: "0", tableLayout: "fixed", width: "100%" }}>
            <thead>
              <tr>
                <th>Agency Name</th>
                <th>Rating</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {ratings &&
                ratings.map((rating) => {
                  return (
                    <tr key={rating.CreditRatingAgencyId}>
                      <td>{rating.CreditRatingAgencyName}</td>
                      <td>{rating.CreditRatingScore}</td>
                      <td>
                        <FormattedNumber
                          value={rating.CreditRatingScoreValue}
                          dataTypeDecimalName="price"
                          decimalScale={2}
                        />
                      </td>
                      <td>
                        <Button color="danger" onClick={() => handleDeleteRating(rating)} size="sm">
                          <i className="fa fa-trash" aria-hidden="true"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </div>
      </td>
    </tr>
  );
};

const Actions = ({ handleAction, range, collapseRow }) => (
  <UncontrolledDropdown>
    <DropdownToggle
      color="link"
      size="lg"
      style={{ border: "none", background: "transparent" }}
      onClick={(e) => e.stopPropagation()}
    >
      <i className="icon-options-vertical icons d-block" />
    </DropdownToggle>
    <DropdownMenu>
      <DropdownItem
        onClick={(e) => {
          e.stopPropagation();
          collapseRow(range.RiskRangeId);
          handleAction(Mode.Update, range);
        }}
      >
        Edit
      </DropdownItem>
      <DropdownItem
        onClick={(e) => {
          e.stopPropagation();
          handleAction(Mode.Delete, range);
        }}
      >
        Delete
      </DropdownItem>
    </DropdownMenu>
  </UncontrolledDropdown>
);

const EmptyOrLoading = ({ loading }) => (
  <tr>
    <td
      colSpan="7"
      style={{
        backgroundColor: "#f5f5f5",
        fontSize: "16px",
        textAlign: "center",
        padding: "20px",
      }}
    >
      {loading ? <LoadingMessage text="Loading credit ranges" /> : "No credit ranges found."}
    </td>
  </tr>
);

export default CreditRatingGrid;
